/**
 * VoiceEngine - Microphone input and voice analysis for warm-up
 * Simplified version for ages 4-6
 */

import type { VoiceProperties } from './types';

export class VoiceEngine {
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private microphone: MediaStreamAudioSourceNode | null = null;
  private stream: MediaStream | null = null;
  private dataArray: Uint8Array | null = null;
  private isActive = false;

  async requestMicrophoneAccess(): Promise<boolean> {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      this.microphone = this.audioContext.createMediaStreamSource(this.stream);
      this.analyser = this.audioContext.createAnalyser();

      // Configure for simple detection
      this.analyser.fftSize = 2048;
      this.analyser.smoothingTimeConstant = 0.8;

      this.microphone.connect(this.analyser);

      const bufferLength = this.analyser.frequencyBinCount;
      this.dataArray = new Uint8Array(bufferLength);

      this.isActive = true;
      return true;
    } catch (error) {
      console.error('Microphone access denied:', error);
      return false;
    }
  }

  getVoiceProperties(): VoiceProperties {
    if (!this.analyser || !this.dataArray || !this.isActive) {
      return {
        pitch: 0.5,
        volume: 0,
        isActive: false,
        timestamp: Date.now()
      };
    }

    // Get frequency data
    this.analyser.getByteFrequencyData(this.dataArray);

    // Calculate volume (amplitude) - 0 to 1
    const volume = this.calculateVolume(this.dataArray);

    // Calculate pitch (dominant frequency) - 0 to 1
    const pitch = this.calculatePitch(this.dataArray);

    return {
      pitch,
      volume,
      isActive: volume > 0.05, // Active if volume above threshold
      timestamp: Date.now()
    };
  }

  private calculateVolume(dataArray: Uint8Array): number {
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i];
    }
    const average = sum / dataArray.length;
    return Math.min(average / 128, 1); // Normalize to 0-1
  }

  private calculatePitch(dataArray: Uint8Array): number {
    // Find the frequency with highest amplitude
    let maxValue = 0;
    let maxIndex = 0;

    // Focus on audible range (skip very low frequencies)
    const startIndex = 5;
    const endIndex = Math.min(dataArray.length / 2, 100);

    for (let i = startIndex; i < endIndex; i++) {
      if (dataArray[i] > maxValue) {
        maxValue = dataArray[i];
        maxIndex = i;
      }
    }

    // Normalize to 0-1 (low pitch to high pitch)
    return Math.min((maxIndex - startIndex) / (endIndex - startIndex), 1);
  }

  stop() {
    this.isActive = false;

    if (this.microphone) {
      this.microphone.disconnect();
    }

    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
    }

    if (this.audioContext) {
      this.audioContext.close();
    }
  }

  isRunning(): boolean {
    return this.isActive;
  }
}
